import React, { useId, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FleetGrid } from '../sso-workspace/fleet-grid'
import { Notice } from '../sso-workspace/fleet-page'
import { ConfirmDialog } from '../sso-workspace/confirm-dialog'
import { TableAgInfinite } from '../table-builder/table-ag-infinite'
import { ColumnFormat, ColumnMenu } from '../table-builder/table-columns'
import { useCursorList } from '../table-builder/use-cursor-list'
import { useGridSelection } from '../table-builder/grid-selection'
import { Menu, MenuEmpty, MenuItem, MenuPermanentlyDelete, MenuRestore } from '../menu/menu'
import { IconPurge, IconRecycle, IconRestore } from '../icon/icon'
import { ErrorReference } from '../error/error-reference'
import { recycleErrorMessage } from '../error/server-message'
import { plural } from '../sso-workspace/format'
import { agoText, utcText } from '../date/local-time'

// A recycle bin that pages on the server, with the shared select mode (R2,
// owner 2026-10-29: "we need the ability to delete and delete all matching
// and we need a recycle bin"). The older hook-driven RecycleList
// (recycle-list.js) reads a whole bin at once; this one is driven by
// functions, so any bin whose server pages by cursor can use it:
//
//   fetchPage(params) -> { items, next }   params: { q?, limit, cursor? }
//   toRow(item) -> a row with id, deletedAt, deletedText and deletedByText
//   restore(selection), purge(selection) -> { changed }
//   empty() -> how many were permanently deleted (a number), or nothing
//   filterOf({ search, since }) -> the filter of "all matching" (the bin's
//     search, and the moment all matching was chosen)
//   children: the kind's columns; Deleted (when, and by whom under it) follows them.
//
// A selection is { ids } or { filter, except? } (grid-selection.js). A row's
// Restore is its pinned icon, Permanently delete is in its ⋮; select mode's
// actions are Restore selected and Permanently delete selected; the card's
// ⋮ has Empty bin. Permanently deleting asks for "delete" to be typed, and
// emptying for "delete all", as recycle-delete.js and recycle-empty.js do.
// After each write the list starts again at the top, select mode ends and
// the card's status line says what happened; a failure says the server's
// coded sentence (errorText, recycleErrorMessage by default) with its
// reference.
//
// noun / items: what a row is called ('session' / 'sessions'). labelOf(row)
// names a row's box and its permanent delete. canWrite: whoever may restore
// and permanently delete; without it the bin is read only. rowTarget(row): a
// page a row opens (a binned record's own page), or nothing.
const NO_FILTERS = {}

export const RecyclePagedList = ({ tableSettings = true, title, queryKey, noun, items, fetchPage, toRow, filterOf, restore, purge, empty, labelOf, canWrite, rowTarget, emptyState, failedText, errorText = recycleErrorMessage, children }) => {
  const history = useHistory()
  const tableRef = useRef()
  const list = useCursorList({ fetchPage, toRow, filters: NO_FILTERS, failedText })
  const selection = useGridSelection({ total: list.total, labelOf })
  const [said, setSaid] = useState(null) // { text, failure }
  const [purging, setPurging] = useState(null) // { selection, words }
  const [emptying, setEmptying] = useState(false)
  const [busy, setBusy] = useState(false)

  const count = n => plural(n, noun, items)
  const chosen = () => selection.request(filterOf({ search: list.search, since: selection.since }))
  const chosenWords = () => (selection.count !== null
    ? count(selection.count)
    : selection.ids.length ? `every ${noun} that matches, except ${count(selection.ids.length)}` : `every ${noun} that matches`)

  // a write: what it said, the list again from the top, select mode over
  const run = async (write, said, failed) => {
    if (busy) return false
    setBusy(true)
    setSaid(null)
    let ok = true
    try {
      setSaid({ text: said(await write()), failure: null })
    } catch (err) {
      ok = false
      setSaid({ text: errorText(err, failed), failure: err })
    }
    setBusy(false)
    selection.done()
    list.reloadFromTop()
    return ok
  }
  const changedOf = answer => Number((answer && answer.changed) || 0)
  const restoreSome = target => run(() => restore(target), answer => `Restored ${count(changedOf(answer))}.`, `That ${noun} was already restored or permanently deleted.`)
  const purgeSome = async target => {
    await run(() => purge(target), answer => `Permanently deleted ${count(changedOf(answer))}.`, `That ${noun} was already permanently deleted.`)
    setPurging(null)
  }
  const emptyAll = async () => {
    await run(() => empty(), purged => (Number.isFinite(purged) ? `Emptied the recycle bin: permanently deleted ${count(purged)}.` : 'Emptied the recycle bin.'), 'The recycle bin was already empty.')
    setEmptying(false)
  }

  const menu = useMemo(() => row => (
    <Menu small>
      <MenuRestore show={canWrite} onClick={() => restoreSome({ ids: [row.id] })} />
      <MenuPermanentlyDelete show={canWrite} onClick={() => setPurging({ selection: { ids: [row.id] }, words: labelOf ? labelOf(row) : `this ${noun}` })} />
    </Menu>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [canWrite, labelOf, noun, busy])

  const selectActions = [
    <MenuItem key="restore-selected" Icon={IconRestore} text="Restore selected" show={canWrite} disabled={selection.none || busy} onClick={() => restoreSome(chosen())} />,
    <MenuItem key="purge-selected" Icon={IconPurge} text="Permanently delete selected" show={canWrite} disabled={selection.none || busy}
      onClick={() => setPurging({ selection: chosen(), words: chosenWords() })} />
  ]
  const cardMenu = [
    <MenuEmpty key="empty" text="empty bin" show={canWrite} disabled={list.total === 0 || busy} onClick={() => setEmptying(true)} />
  ]
  const notice = said
    ? said.failure
      ? <span className="text-danger" role="alert">{said.text} <ErrorReference error={said.failure} compact /></span>
      : <span className="sw-hint" role="status">{said.text}</span>
    : null

  return (
    <>
      <FleetGrid title={title} subtitle="Newest deleted first" tableRef={tableRef} menu={cardMenu} notice={notice} tableSettings={tableSettings}
        selection={canWrite && !list.error ? selection : null} selectActions={selectActions}>
        {list.error
          ? <Notice tone="bad" role="alert">{list.error} <button type="button" className="btn btn-sm sw-btn ms-1" onClick={list.retry}>Try again</button><ErrorReference error={list.failure} /></Notice>
          : <TableAgInfinite ref={tableRef} queryKey={queryKey} onGetRows={list.getRows} reloadKey={list.reloadKey} refreshKey={list.refreshKey} selection={selection}
            onRowClicked={row => { const to = row && rowTarget && rowTarget(row); if (to) history.push(to) }}
            emptyState={emptyState || { Icon: IconRecycle, title: 'The recycle bin is empty', message: `${items[0].toUpperCase()}${items.slice(1)} you delete appear here, and can be restored until they are permanently deleted.` }}>
            {children}
            <ColumnFormat field="deletedText" header="Deleted" sortable={false} textMax={200} stacked render={deletedCell} />
            <ColumnMenu menu={menu} />
          </TableAgInfinite>}
      </FleetGrid>

      {purging &&
        <TypedConfirm title={`Permanently delete ${purging.words}?`} confirmValue="delete" busy={busy}
          warning={`You are about to permanently delete ${purging.words}. This can't be undone.`}
          onCancel={() => setPurging(null)} onConfirm={() => purgeSome(purging.selection)} />}
      {emptying &&
        <TypedConfirm title="Empty the recycle bin?" confirmValue="delete all" busy={busy}
          warning={`You are about to permanently delete the following items: every ${noun} in the recycle bin. This can't be undone.`}
          onCancel={() => setEmptying(false)} onConfirm={emptyAll} />}
    </>
  )
}

// Grid cells (module-level, so AG Grid keeps its cells). When and who are one
// column, so a bin fits a 1280px window (owner 2026-10-29): how long ago,
// with the exact time on hover, and who under it.
const deletedCell = cell => {
  const row = cell.data
  if (!row || !row.deletedAt) return ''
  return (
    <span className="ak-name-cell">
      <span title={`${row.deletedText} · ${utcText(row.deletedAt)}`}>{agoText(row.deletedAt)}</span>
      {row.deletedByText && ' '}
      {row.deletedByText && <span className="sw-cell-sub text-truncate" title={row.deletedByText}>by {row.deletedByText}</span>}
    </span>
  )
}

// A permanent delete's confirmation: the word to type ("delete", "delete
// all"; any case, spaces trimmed, as FormNewTextConfirm takes it) holds the
// button until it is typed.
export const TypedConfirm = ({ title, warning, confirmValue, busy, onCancel, onConfirm }) => {
  const id = useId()
  const [typed, setTyped] = useState('')
  const matches = typed.trim().toLowerCase() === confirmValue.trim().toLowerCase()
  return (
    <ConfirmDialog open title={title} body={warning} confirmText="Permanently Delete" busy={busy} confirmDisabled={!matches} onCancel={onCancel} onConfirm={() => matches && onConfirm()}>
      <div className="mb-3">
        <label className="sw-label" htmlFor={id}>{`Type ${confirmValue} to confirm`}</label>
        <input id={id} className="form-control" type="text" autoComplete="off" placeholder={confirmValue} value={typed} onChange={e => setTyped(e.target.value)} />
      </div>
    </ConfirmDialog>
  )
}
